import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { municipalitiesService } from '../api';
import { gradovi } from '../utils/constants';

const MunicipalitiesContext = createContext(null);
const STORAGE_KEY = 'municipalitiesCache';

const readCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export function MunicipalitiesProvider({ children }) {
  const [remote, setRemote] = useState(readCache);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await municipalitiesService.getAll();
      const list = Array.isArray(res.data) ? res.data : [];
      setRemote(list);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
    } catch (err) {
      console.error('Greška pri učitavanju opština:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Dok baza nije popunjena, koristimo statičku listu kao fallback.
  // Set-union: prikazuje sve poznate opštine bez duplikata.
  const names = useMemo(() => {
    const fromDb = remote.map((m) => m.name).filter(Boolean);
    const seen = new Set();
    const ordered = [];
    for (const n of [...fromDb, ...gradovi]) {
      if (!seen.has(n)) {
        seen.add(n);
        ordered.push(n);
      }
    }
    return ordered.sort((a, b) => a.localeCompare(b, 'sr'));
  }, [remote]);

  return (
    <MunicipalitiesContext.Provider value={{ names, items: remote, isLoading, refresh }}>
      {children}
    </MunicipalitiesContext.Provider>
  );
}

export function useMunicipalities() {
  const ctx = useContext(MunicipalitiesContext);
  if (!ctx) throw new Error('useMunicipalities must be used within MunicipalitiesProvider');
  return ctx;
}
